/**
 * ImageLabelFilterStep — step 3 of the ABR filter form dialog.
 * Allows selecting image labels with score thresholds and an AND/OR modifier.
 */

import { LabelSelector, type Label, type ScoreConfig, type ScoreLabelOption, getScoreDescription, getScoreLabel } from '@core/components/LabelSelector';
import type { LabelFilterInput, FilterOperator } from '@moderation/domain/AbrFilter';
import { Heading, Select, Text } from '@soteria/design-system-ve';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useCallback, useMemo } from 'react';

import * as styles from './AbrFilterFormDialog.css';
import { MODIFIER_OPTIONS, isFilterOperator } from './abrFilterConstants';
import { imageFiltersToLabels, labelsToImageFilters } from './labelFilterConversions';

export interface ImageLabelFilterStepProps {
  imageLabelFilters: LabelFilterInput[];
  imageLabelFilterModifier: FilterOperator;
  standardLabels: StandardLabel[];
  labelsLoading: boolean;
  onImageLabelFiltersChange: (filters: LabelFilterInput[]) => void;
  onImageLabelFilterModifierChange: (modifier: FilterOperator) => void;
}

const SCORE_CONFIG: ScoreConfig = {
  mode: 'score',
  min: 0,
  max: 100,
  defaultValue: 70,
  showDescription: true,
  getValueLabel: getScoreLabel,
  getValueDescription: getScoreDescription,
};

export function ImageLabelFilterStep({
  imageLabelFilters,
  imageLabelFilterModifier,
  standardLabels,
  labelsLoading,
  onImageLabelFiltersChange,
  onImageLabelFilterModifierChange,
}: ImageLabelFilterStepProps) {
  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Image)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [standardLabels]
  );

  const imageLabelsForSelector = useMemo(() => imageFiltersToLabels(imageLabelFilters), [imageLabelFilters]);

  const handleImageLabelsChange = useCallback((labels: Label[]) => onImageLabelFiltersChange(labelsToImageFilters(labels)), [onImageLabelFiltersChange]);

  return (
    <div className={styles.stepContentStyles}>
      <div className={styles.fieldGroupStyles}>
        <Heading level="h3">Image Label Filters</Heading>
        <Text className={styles.toggleDescriptionStyles}>
          Match cases with image labels at or above the score threshold. Cases are matched using the selected modifier (AND/OR).
        </Text>
      </div>

      <div className={styles.fieldGroupStyles}>
        <Text className={styles.fieldLabelStyles}>Modifier</Text>
        <div className={styles.modifierRowStyles}>
          <Select
            aria-label="Image label filter modifier"
            options={MODIFIER_OPTIONS}
            value={imageLabelFilterModifier}
            onChange={v => {
              if (isFilterOperator(v)) onImageLabelFilterModifierChange(v);
            }}
          />
        </div>
      </div>

      <div className={styles.fieldGroupStyles}>
        {imageLabelOptions.length > 0 ? (
          <LabelSelector
            enableBulkSelection
            availableLabels={imageLabelOptions}
            config={SCORE_CONFIG}
            emptyStateMessage="No image labels selected. Add labels to filter by image content scores."
            loading={labelsLoading}
            selectedLabels={imageLabelsForSelector}
            title="Image Labels"
            onChange={handleImageLabelsChange}
          />
        ) : (
          <div aria-live="polite" role="status">
            {labelsLoading ? <Text>Loading image labels...</Text> : <Text>No image labels available. Please configure labels in system settings.</Text>}
          </div>
        )}
      </div>
    </div>
  );
}
